import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, PhoneIcon, CalendarIcon, UsersIcon } from 'lucide-react';
interface RegisterProps {
  navigate: (screen: string, param?: string) => void;
}
export function Register({
  navigate
}: RegisterProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('otp-verification', 'register'); // Go to OTP verification before KYC
    }, 1500);
  };
  return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-2779">
      {/* Header Background */}
      <div className="absolute top-0 left-0 right-0 h-56 bg-[#FFD700] rounded-b-[3rem] shadow-lg shadow-[#FFD700]/20" data-id="element-2780" />

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pt-12 pb-safe z-10 overflow-y-auto" data-id="element-2781">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center mb-10" data-id="element-2782">
          <h1 className="text-3xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-2783">
            Create Account
          </h1>
          <p className="text-[#0D3B3B]/70 text-sm text-center max-w-[260px] font-medium" data-id="element-2784">
            Join Skydd to start building and protecting your wealth.
          </p>
        </motion.div>

        {/* Register Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-[2rem] p-6 shadow-xl shadow-emerald-900/5 flex-1 flex flex-col max-w-md w-full mx-auto mb-6" data-id="element-2785">
          <form onSubmit={handleRegister} className="flex-1 flex flex-col" data-id="element-2786">
            <div className="space-y-4 mb-6" data-id="element-2787">
              {/* Name Inputs */}
              <div className="grid grid-cols-2 gap-4" data-id="element-2788">
                <div className="space-y-1.5" data-id="element-2789">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2790">
                    First Name
                  </label>
                  <div className="relative flex items-center" data-id="element-2791">
                    <div className="absolute left-4 text-gray-400" data-id="element-2792">
                      <UserIcon className="w-5 h-5" data-id="element-2793" />
                    </div>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Chinedu" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2794" />
                  </div>
                </div>
                <div className="space-y-1.5" data-id="element-2795">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2796">
                    Last Name
                  </label>
                  <div className="relative flex items-center" data-id="element-2797">
                    <div className="absolute left-4 text-gray-400" data-id="element-2798">
                      <UserIcon className="w-5 h-5" data-id="element-2799" />
                    </div>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Okafor" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2800" />
                  </div>
                </div>
              </div>

              {/* Email Input */}
              <div className="space-y-1.5" data-id="element-2801">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2802">
                  Email Address
                </label>
                <div className="relative flex items-center" data-id="element-2803">
                  <div className="absolute left-4 text-gray-400" data-id="element-2804">
                    <MailIcon className="w-5 h-5" data-id="element-2805" />
                  </div>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2806" />
                </div>
              </div>

              {/* Phone Input */}
              <div className="space-y-1.5" data-id="element-2807">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2808">
                  Phone Number
                </label>
                <div className="relative flex items-center" data-id="element-2809">
                  <div className="absolute left-4 text-gray-400" data-id="element-2810">
                    <PhoneIcon className="w-5 h-5" data-id="element-2811" />
                  </div>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="0800 000 0000" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2812" />
                </div>
              </div>

              {/* DOB Input */}
              <div className="space-y-1.5" data-id="element-2813">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2814">
                  Date of Birth
                </label>
                <div className="relative flex items-center" data-id="element-2815">
                  <div className="absolute left-4 text-gray-400" data-id="element-2816">
                    <CalendarIcon className="w-5 h-5" data-id="element-2817" />
                  </div>
                  <input type="date" value={dob} onChange={e => setDob(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2818" />
                </div>
              </div>

              {/* Gender Input */}
              <div className="space-y-1.5" data-id="element-2819">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2820">
                  Gender
                </label>
                <div className="relative flex items-center" data-id="element-2821">
                  <div className="absolute left-4 text-gray-400" data-id="element-2822">
                    <UsersIcon className="w-5 h-5" data-id="element-2823" />
                  </div>
                  <select value={gender} onChange={e => setGender(e.target.value)} className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all appearance-none" required data-id="element-2824">
                    <option value="" disabled data-id="element-2825">
                      Select Gender
                    </option>
                    <option value="male" data-id="element-2826">Male</option>
                    <option value="female" data-id="element-2827">Female</option>
                    <option value="other" data-id="element-2828">Other</option>
                  </select>
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-1.5" data-id="element-2829">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2830">
                  Password
                </label>
                <div className="relative flex items-center" data-id="element-2831">
                  <div className="absolute left-4 text-gray-400" data-id="element-2832">
                    <LockIcon className="w-5 h-5" data-id="element-2833" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Create a secure password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-3.5 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2834" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2835">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2836" /> : <EyeIcon className="w-5 h-5" data-id="element-2837" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="mb-6" data-id="element-2838">
              <p className="text-xs text-gray-500 text-center leading-relaxed" data-id="element-2839">
                By creating an account, you agree to our{' '}
                <span className="font-bold text-[#0D3B3B]" data-id="element-2840">
                  Terms of Service
                </span>{' '}
                and{' '}
                <span className="font-bold text-[#0D3B3B]" data-id="element-2841">Privacy Policy</span>
                .
              </p>
            </div>

            {/* Register Button */}
            <button type="submit" disabled={isLoading || !firstName || !lastName || !email || !phone || !dob || !gender || !password} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 mt-auto ${isLoading || !firstName || !lastName || !email || !phone || !dob || !gender || !password ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-emerald-900/20'}`} data-id="element-2842">
              {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2843" /> : <>
                  Create Account
                  <ArrowRightIcon className="w-5 h-5" data-id="element-2844" />
                </>}
            </button>

            {/* Login Link */}
            <div className="mt-6 text-center" data-id="element-2845">
              <p className="text-sm text-gray-500" data-id="element-2846">
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('login')} className="font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2847">
                  Log in
                </button>
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}